/**
 * Client-side composition of a shareable "Before | After" PNG with PrimeFace branding.
 * Works entirely in the browser (canvas) - no upload, no server round-trip.
 */

const COLORS = {
  ink: '#07070A',
  panel: '#111118',
  border: '#2A2A36',
  gold: '#F3B33D',
  white: '#FFFFFF',
  muted: 'rgba(255,255,255,0.45)',
};

const PANEL = 1024;
const GAP = 28;
const PAD = 56;
const FOOTER = 150;
const RADIUS = 36;

const loadImage = (src) => new Promise((resolve, reject) => {
  if (!src) { reject(new Error('missing image source')); return; }
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('image failed to load'));
  img.src = src;
});

const roundedRectPath = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

const drawContain = (ctx, img, x, y, size) => {
  const scale = Math.min(size / img.width, size / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, x + (size - w) / 2, y + (size - h) / 2, w, h);
};

const drawPanel = (ctx, img, x, y, label, variant) => {
  ctx.save();
  roundedRectPath(ctx, x, y, PANEL, PANEL, RADIUS);
  ctx.fillStyle = COLORS.panel;
  ctx.fill();
  ctx.clip();
  drawContain(ctx, img, x, y, PANEL);
  ctx.restore();

  ctx.save();
  roundedRectPath(ctx, x, y, PANEL, PANEL, RADIUS);
  ctx.lineWidth = 2;
  ctx.strokeStyle = COLORS.border;
  ctx.stroke();
  ctx.restore();

  // label pill
  ctx.save();
  ctx.font = '600 30px Manrope, Helvetica, Arial, sans-serif';
  const textWidth = ctx.measureText(label).width;
  const pillW = textWidth + 48;
  const pillH = 56;
  const px = x + 28;
  const py = y + 28;
  roundedRectPath(ctx, px, py, pillW, pillH, pillH / 2);
  ctx.fillStyle = variant === 'after' ? COLORS.gold : 'rgba(0,0,0,0.72)';
  ctx.fill();
  ctx.fillStyle = variant === 'after' ? COLORS.ink : COLORS.white;
  ctx.textBaseline = 'middle';
  ctx.fillText(label, px + 24, py + pillH / 2 + 1);
  ctx.restore();
};

const drawFooter = (ctx, logo, width, top, language, subtitle) => {
  const y = top;
  ctx.save();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(PAD, y);
  ctx.lineTo(width - PAD, y);
  ctx.stroke();

  const logoSize = 72;
  const logoX = PAD;
  const logoY = y + (FOOTER - logoSize) / 2;
  if (logo) {
    ctx.save();
    roundedRectPath(ctx, logoX, logoY, logoSize, logoSize, 18);
    ctx.clip();
    drawContain(ctx, logo, logoX, logoY, logoSize);
    ctx.restore();
    ctx.save();
    roundedRectPath(ctx, logoX, logoY, logoSize, logoSize, 18);
    ctx.strokeStyle = 'rgba(243,179,61,0.4)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }

  const textX = logoX + (logo ? logoSize + 24 : 0);
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.white;
  ctx.font = '600 44px "EB Garamond", Georgia, serif';
  ctx.fillText('PrimeFace', textX, y + FOOTER / 2 + 2);
  ctx.fillStyle = COLORS.muted;
  ctx.font = '500 24px Manrope, Helvetica, Arial, sans-serif';
  ctx.fillText(
    subtitle || (language === 'fr' ? 'Simulation Avant / Apr\u00e8s g\u00e9n\u00e9r\u00e9e par votre coach IA' : 'Before / After simulation generated by your AI coach'),
    textX, y + FOOTER / 2 + 40,
  );

  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.muted;
  ctx.font = '500 22px Manrope, Helvetica, Arial, sans-serif';
  ctx.fillText('PrimeFace \u00a9 2026 OpenMeta', width - PAD, y + FOOTER / 2 + 10);
  ctx.restore();
};

/**
 * @param {{beforeSrc: string, afterSrc: string, language?: string, logoSrc?: string, labels?: [string, string], subtitle?: string}} params
 *   labels: custom panel labels (defaults to AVANT / APRES); subtitle: custom footer line.
 * @returns {Promise<Blob>} PNG blob
 */
export const buildBeforeAfterImage = async ({ beforeSrc, afterSrc, language = 'fr', logoSrc = '/logo.jpeg', labels = null, subtitle = null }) => {
  const [before, after, logo] = await Promise.all([
    loadImage(beforeSrc),
    loadImage(afterSrc),
    loadImage(logoSrc).catch(() => null),
  ]);

  if (document.fonts?.load) {
    try {
      await Promise.all([
        document.fonts.load('600 44px "EB Garamond"'),
        document.fonts.load('600 30px Manrope'),
      ]);
    } catch (e) {
      // fall back to system fonts silently
    }
  }

  const width = PAD * 2 + PANEL * 2 + GAP;
  const height = PAD + PANEL + FOOTER;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = COLORS.ink;
  ctx.fillRect(0, 0, width, height);

  const panelLabels = labels || (language === 'fr' ? ['AVANT', 'APR\u00c8S'] : ['BEFORE', 'AFTER']);
  drawPanel(ctx, before, PAD, PAD, panelLabels[0], 'before');
  drawPanel(ctx, after, PAD + PANEL + GAP, PAD, panelLabels[1], 'after');

  // gold divider between panels
  ctx.fillStyle = COLORS.gold;
  ctx.fillRect(PAD + PANEL + GAP / 2 - 2, PAD + 120, 4, PANEL - 240);

  drawFooter(ctx, logo, width, PAD + PANEL, language, subtitle);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('canvas export failed'))), 'image/png');
  });
};

/** Trigger a browser download for a Blob without touching React-managed DOM. */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const beforeAfterFilename = (language = 'fr') => {
  const stamp = new Date().toISOString().slice(0, 10);
  return language === 'fr' ? `primeface-avant-apres-${stamp}.png` : `primeface-before-after-${stamp}.png`;
};

export const comparisonFilename = (language = 'fr') => {
  const stamp = new Date().toISOString().slice(0, 10);
  return language === 'fr' ? `primeface-progression-${stamp}.png` : `primeface-progress-${stamp}.png`;
};
